import { useState } from 'react';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import {
  getReactSelectValue,
  ReactSelectInput,
} from '@hivebrite/design-system/Inputs';

import { galleryImageRecommendedSize } from 'hb-builder/components/BuilderComponents/constants/imageSizes';
import ImageSetter from 'hb-builder/components/common/ImageSetter';
import DeleteItemModal from 'hb-builder/components/common/modal/DeleteItemModal';
import EditImageSource from 'hb-builder/components/common/SettingsLibrary/CommonSettings/EditImageSource';
import TextSetting from 'hb-builder/components/common/SettingsLibrary/CommonSettings/TextSetting';
import InputTitle from 'hb-builder/components/common/SettingsLibrary/SettingsDecoration/InputTitle';
import SettingBlock from 'hb-builder/components/common/SettingsLibrary/SettingsDecoration/SettingBlock';
import {
  BACKGROUND_POSITION,
  positionOptions,
} from 'hb-builder/constants/imageArrangement';
import { useBuilderContext, useSortableListContext } from 'hb-builder/hooks';
import type { ComponentSettingsTab } from 'hb-builder/types/settings';

interface ContentItemProps extends ComponentSettingsTab {
  removeItemOpen: (id: string) => void;
}

const ContentItem = ({ item, onChange, removeItemOpen }: ContentItemProps) => {
  const { getParentItem } = useBuilderContext();
  const { isItemOpen } = useSortableListContext();

  const parentItem = getParentItem(item.id);

  const [positionValue, setPositionValue] = useState(
    item.attributes?.backgroundPosition || BACKGROUND_POSITION.CENTER,
  );

  const canDeleteItem =
    (parentItem.type === 'InfiniteSlider' && parentItem.children.length > 3) ||
    (parentItem.type === 'SliderItem' && parentItem.children.length > 1);

  const settingsProps = { item, onChange };
  const hasImage = Boolean(item?.attributes?.url);

  const handleChangePosition = (e) => {
    setPositionValue(e.value);

    onChange({
      id: item.id,
      settingKey: 'attributes.backgroundPosition',
      value: e.value,
    });
  };

  const positionValueSelected = getReactSelectValue(
    positionOptions,
    positionValue,
  );

  return (
    <SettingBlock boxProps={{ mt: 1, mb: 4 }}>
      <ImageSettingWrapper>
        <InputTitle id="settingsImagePosition" label={__('Image position')} />
        <ReactSelectInput
          id="settingsImagePosition"
          name="settingsImagePosition"
          onChange={handleChangePosition}
          options={positionOptions}
          value={positionValueSelected}
          width={155}
        />
      </ImageSettingWrapper>
      <Box my={2}>
        {s__(
          'Builder|Recommended %{size}px. The file must not exceed 2.5 MB',
        ).i__({
          size: galleryImageRecommendedSize[parentItem.type],
        })}
      </Box>
      <EditImageSource hasImage={hasImage} id={item.id} />
      <TextSetting
        dataTestId="gallery-alt-text-setting"
        description={s__(
          "Builder|This will appear if the image can't be displayed",
        )}
        settingKey="attributes.imageAlt"
        title={__('Alternative text')}
        {...settingsProps}
      />
      <TextSetting
        description={__('The text must not exceed 50 characters')}
        maxLength={50}
        settingKey="attributes.caption"
        title={__('Caption')}
        verticalAlign
        {...settingsProps}
      />
      {canDeleteItem && (
        // @ts-expect-error not typed
        <DeleteItemModal
          id={item.id}
          itemName="image"
          onConfirm={() => removeItemOpen(item.id)}
        />
      )}
      {isItemOpen(item.id) && (
        <ImageSetter
          attributeName="galleryImage"
          item={item}
          imageSize={
            galleryImageRecommendedSize?.[parentItem?.type] ||
            galleryImageRecommendedSize.SliderItem
          }
        />
      )}
    </SettingBlock>
  );
};

export default ContentItem;

const ImageSettingWrapper = styled(Box).attrs(() => ({
  alignItems: 'center',
  justifyContent: 'space-between',
}))``;
